import {createSlice} from '@reduxjs/toolkit';
import type {PayloadAction} from '@reduxjs/toolkit';
import {EditorProject, EncoderProject} from '../../types';

export interface EditorState {
  encoderProgress: number;
  caretPosition: number;
  scrollPosition: number;
  pixelsPerSecond: number;
  jobs: BackgroundRenderJob[];
  jobsProgress: {[key: string]: number};
  isPlaying: boolean;
  videoScrollPosition: number;
  videoCaretPosition: number;
  scrollParams: {
    totalWidth: number;
    totalScroll: number;
  };
  canScroll: boolean;
  activeLayer: string;
  prevActiveLayer: string;
  scroll: number;
  totalTime: number;
  showEditor: boolean;
  showComments: boolean;
  isRendering: boolean;
  renderProgress: number;
  renderTime: number;
  renderOutputURL?: string;
  ffmpegLogs: string;
  ffmpegError: boolean;
  showConfig?: true;
  editorForm?: EditorProject['editor'];
}

export interface BackgroundRenderJob {
  id: string;
  encoder?: EncoderProject;
  progress?: number;
}

const initialState: EditorState = {
  encoderProgress: 0,
  caretPosition: 0,
  scrollPosition: 0,
  pixelsPerSecond: 6,
  jobs: [],
  jobsProgress: {},
  isPlaying: false,
  videoCaretPosition: 0,
  videoScrollPosition: 0,
  scrollParams: {
    totalWidth: 0,
    totalScroll: 0,
  },
  canScroll: true,
  activeLayer: '1',
  prevActiveLayer: '1',
  scroll: 0,
  totalTime: 0,
  showEditor: true,
  showComments: false,
  isRendering: false,
  renderProgress: 0,
  renderTime: 0,
  ffmpegLogs: '',
  ffmpegError: false,
};

export const editorSlice = createSlice({
  name: 'editor',
  initialState,
  reducers: {
    setEncoderProgress: (state, action: PayloadAction<number>) => {
      state.encoderProgress = action.payload;
    },
    setActiveLayer: (
      state,
      action: PayloadAction<{
        activeLayerId: string;
        caretPosition: number;
        scrollPosition: number;
      }>,
    ) => {
      if (state.isPlaying === true) {
        state.videoCaretPosition = action.payload.caretPosition;
        state.videoScrollPosition = action.payload.scrollPosition;
        state.activeLayer = action.payload.activeLayerId;
        // console.info("active update video", state.videoCaretPosition, state.videoScrollPosition)
      }
    },
    setCaretPosition: (state, action: PayloadAction<number>) => {
      state.caretPosition = action.payload;
      if (state.isPlaying === false) {
        state.videoCaretPosition = action.payload;
      }
    },
    setScrollPosition: (state, action: PayloadAction<number>) => {
      state.scrollPosition = action.payload;
      if (state.isPlaying === false) {
        state.videoScrollPosition = action.payload;
      }
    },
    setPixelsPerSecond: (state, action: PayloadAction<number>) => {
      state.pixelsPerSecond = action.payload;
    },
    createBackgroundRenderJob: (
      state,
      action: PayloadAction<BackgroundRenderJob>,
    ) => {
      state.jobs.push(action.payload);
    },
    setJobProgress: (
      state,
      action: PayloadAction<BackgroundRenderJob>,
    ) => {
      state.jobsProgress[action.payload.id] =
        action.payload.progress!;
    },
    setIsPlaying: (state, action: PayloadAction<boolean>) => {
      state.isPlaying = action.payload;
      if (action.payload === false) {
        state.videoCaretPosition = state.caretPosition;
        state.videoScrollPosition = state.scrollPosition;
      }
    },
    setTotalTime: (state, action: PayloadAction<number>) => {
      state.totalTime = action.payload;
    },
    setScrollParams: (
      state,
      action: PayloadAction<{
        totalWidth: number;
        totalScroll: number;
      }>,
    ) => {
      state.scrollParams = action.payload;
      const canScroll =
        state.scrollParams.totalScroll >
        state.scrollParams.totalWidth;
      const newscroll =
        state.scrollParams.totalScroll -
        state.scrollParams.totalWidth;
      if (
        state.canScroll !== canScroll &&
        state.isPlaying === false
      ) {
        // console.info('update scroll', newscroll, canScroll)
        state.canScroll = canScroll;
      }
      if (state.scroll !== newscroll && !isNaN(newscroll)) {
        state.scroll = newscroll;
      }
    },
    setShowEditor: (state, action: PayloadAction<boolean>) => {
      state.showEditor = action.payload;
    },
    setShowComments: (state, action: PayloadAction<boolean>) => {
      state.showComments = action.payload;
    },
    setIsRendering: (state, action: PayloadAction<boolean>) => {
      state.isRendering = action.payload;
      if (state.isRendering) {
        state.ffmpegLogs = '';
        state.ffmpegError = false;
      }
    },
    setRenderUrl: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      state.renderOutputURL = action.payload;
    },
    setRenderProgress: (
      state,
      action: PayloadAction<{progress: number; time: number}>,
    ) => {
      state.renderProgress = action.payload.progress;
      state.renderTime = action.payload.time / 1000000;
      // console.info('renderTime', state.renderTime)
      state.caretPosition = state.renderTime * state.pixelsPerSecond;
      state.videoCaretPosition = state.caretPosition;
    },
    addNewLogLine: (state, action: PayloadAction<string>) => {
      state.ffmpegLogs += '\n' + action.payload;
      if (action.payload.toLowerCase().search('error') > -1)
        state.ffmpegError = true;
    },
    setRenderingError: (state, action: PayloadAction<boolean>) => {
      state.ffmpegError = action.payload;
    },
    setShowConfig: (
      state,
      action: PayloadAction<true | undefined>,
    ) => {
      state.showConfig = action.payload;
    },
    setEditorForm: (
      state,
      action: PayloadAction<EditorProject['editor'] | undefined>,
    ) => {
      state.editorForm = action.payload;
    },
  },
});

export const {
  setEncoderProgress,
  setCaretPosition,
  setScrollPosition,
  setPixelsPerSecond,
  setIsPlaying,
  setScrollParams,
  setActiveLayer,
  setShowComments,
  setShowEditor,
  setIsRendering,
  setRenderUrl,
  setRenderProgress,
  addNewLogLine,
  setShowConfig,
  setEditorForm,
  setRenderingError,
} = editorSlice.actions;

export default editorSlice.reducer;
